"use client";

import { useEffect } from "react";

import { consentChoice } from "@/lib/analytics-consent";
import { useSession } from "@/lib/auth-client";
import {
  dpopApi,
  publicApi,
  setRequestFailureReporter,
} from "@/lib/dpop-client";

// First-party journey analytics (docs/journey-analytics-checklist.md).
//
//   trackEvent(name, props) → localStorage queue → POST /v1/journeys/events/batch
//   arrivals and the signed-in check-in → POST /v1/journeys/arrivals
//
// Every event must be cataloged in fexrapi's journey_event_definitions with
// the same version, privacy tier and keys (fexrapi tests/test_journey_catalog.py
// reads JOURNEY_EVENTS below). Properties are categorical, counts or bands:
// never typed text, amounts or addresses; fexrapi rejects those.

// ── Event registry ───────────────────────────────────────────────────────────
// v: catalog version (default 1) · tier: privacy tier (default "core") ·
// sample: fraction of occurrences sent (default 1).
export const JOURNEY_EVENTS = {
  // Sessions and navigation
  "app.session.started": { keys: [] },
  "app.session.foregrounded": { keys: [] },
  "app.session.backgrounded": { keys: [] },
  "screen.viewed": { keys: ["route"] },
  "screen.exited": { keys: ["route", "dwell_ms", "exit_reason"] },
  "discovery.visit.qualified": { keys: ["dwell_ms", "max_scroll_depth"] },
  // Landing and marketing
  "landing.cta_clicked": { keys: ["cta", "section"] },
  "landing.ask_submitted": { keys: ["origin", "example_index", "authed"] },
  "landing.section_viewed": { keys: ["section"] },
  "landing.pricing_viewed": { keys: ["plan"] },
  // Auth
  "auth.started": { keys: ["method", "mode", "has_from"] },
  "auth.email_link_sent": { keys: ["mode"] },
  "auth.failed": { keys: ["method", "reason"] },
  "auth.completed": { keys: ["method", "is_new_account", "from_route"] },
  "auth.signed_out": { keys: [] },
  // Discover / Ask
  "ask.question_sent": {
    keys: ["mode", "subject_count", "vault_count", "origin", "thread_turn"],
  },
  "ask.answer_rendered": {
    keys: ["state", "duration_band", "source_count", "had_chart"],
  },
  "ask.thread_opened": { keys: ["from"] },
  "ask.recents_scope_changed": { keys: ["scope"] },
  "insight.impression": { keys: ["rank", "actionable", "freshness_bucket"] },
  "insight.opened": { keys: ["dwell_ms"] },
  "insight.saved": { keys: ["collection"] },
  "insight.dismissed": { keys: ["reason"] },
  "insight.feedback_submitted": { keys: ["feedback_type"] },
  "insight.action_selected": { keys: ["action_type"] },
  // Market Prices, Holdings Graph
  "markets.searched": { keys: ["result_band"] },
  "markets.asset_opened": { keys: ["asset_group", "rank_band", "from"] },
  "markets.topic_opened": { keys: ["topic"] },
  "markets.graph_command": { keys: ["command"] },
  // Share moments (docs/markets-share-moments-plan.md): a live moment seen, its
  // share opened, and a recipient landing on it (changed = then ≠ now).
  "markets.moment_viewed": { keys: ["moment", "page"] },
  "markets.moment_share_opened": { keys: ["moment", "page", "signed_in"] },
  "markets.moment_landed": { keys: ["moment", "page", "changed"] },
  // Whale Watch
  "whales.board_viewed": { keys: ["sort", "filter"] },
  "whales.trader_opened": { keys: ["rank_band", "from"] },
  "whales.trader_followed": { keys: ["plan", "gated"] },
  "whales.trader_unfollowed": { keys: ["plan"] },
  "whales.feed_item_opened": { keys: ["move_type"] },
  // Market Signals
  "signals.tab_viewed": { keys: ["group", "view"] },
  "signals.widget_interacted": { keys: ["view", "action"] },
  "signals.screener_run": { keys: ["filter_count", "result_band"] },
  "signals.watchlist_changed": { keys: ["action", "size_band"] },
  "alert.created": { keys: ["alert_type"] },
  "digest.opened": { keys: ["channel"] },
  // Strategies and trading
  "strategy.created": { keys: ["strategy_type", "origin"] },
  "strategy.activated": { keys: ["strategy_type"] },
  "strategy.paused": { keys: ["strategy_type", "age_band"] },
  "strategy.deleted": { keys: ["strategy_type", "age_band"] },
  "strategies.replay_opened": { keys: ["scope", "window"] },
  "strategies.template_opened": { keys: ["template_kind"] },
  "strategies.template_copied": { keys: ["template_kind"] },
  "trade.intent_created": { keys: ["strategy_type", "trigger_type"] },
  // Trading funnel, shared with the app (fexrapi 20261215_journey_trading_funnel)
  "trading.setup_opened": { keys: ["entry", "start_step", "blocked"] },
  "trading.setup_failed": { keys: ["entry", "step"] },
  "trading.agent_activated": { keys: ["entry"] },
  "trading.deposit_completed": { keys: ["credited"] },
  "trading.strategy_started": { keys: ["strategy_type", "first", "seeded"] },
  // Portfolio and Explore (docs/trading-ia-and-sharing-plan.md)
  "explore.filter_changed": { keys: ["type", "has_asset"] },
  "explore.view_changed": { keys: ["view"] },
  "portfolio.agent_panel_opened": { keys: ["from"] },
  "snapshot.landed": { keys: ["subject", "found", "mine", "amounts"] },
  "copy.leader_viewed": { keys: ["surface"] },
  // Vaults
  "vault.page_viewed": { keys: ["role", "from"] },
  "vault.app_handoff_clicked": { keys: ["action"] },
  "vault.console_action": { keys: ["action"] },
  // Paywall, checkout, payments
  "paywall.viewed": { keys: ["feature", "plan_required", "trigger"] },
  "paywall.dismissed": { keys: ["feature"] },
  "paywall.cta_clicked": { keys: ["plan", "rail", "feature"] },
  "checkout.wallet_connect_failed": { keys: ["reason", "rail"] },
  "payments.deposit_started": { keys: ["rail"] },
  "payments.deal_created": { keys: ["deal_type"] },
  "pay.page_viewed": { keys: ["state", "from"] },
  "billing.coupon_applied": { keys: ["result"] },
  // Settings, workspace, agents, developer
  "settings.section_viewed": { keys: ["tab"] },
  "settings.changed": { keys: ["setting"] },
  "appearance.changed": { keys: ["theme", "contrast", "motion"] },
  "workspace.switched": { keys: ["count_band"] },
  "workspace.member_invited": { keys: ["role"] },
  "agent.role_assigned": { keys: ["persona", "role"] },
  "agent.detail_opened": { keys: ["persona"] },
  "developer.api_key_created": { keys: [] },
  "webhook.created": { keys: ["event_family"] },
  // Help and support
  "help.searched": { keys: ["result_band"] },
  "help.article_opened": { keys: ["article"] },
  "help.next_step_clicked": { keys: ["step"] },
  "support.request_submitted": { keys: ["category"] },
  // Sharing
  shared_link_visited: { keys: ["link_type", "link_id", "ref", "src"] },
  link_copied: { v: 2, keys: ["link_id", "surface", "link_type"] },
  link_shared: { v: 2, keys: ["link_id", "surface", "link_type"] },
  "share.opened": { keys: ["surface", "link_kind", "signed_in"] },
  "referral.share.completed": { keys: ["share_result"] },
  "link_entry.viewed": { keys: ["state", "link_kind"] },
  // PWA and push
  "pwa.install_available": { keys: ["platform"] },
  "pwa.install_prompted": { keys: ["platform"] },
  "pwa.install_prompt_result": { keys: ["platform", "outcome"] },
  "pwa.installed": { keys: ["platform"] },
  "pwa.manual_install_help_opened": { keys: ["platform"] },
  "pwa.standalone_launch": { keys: ["platform"] },
  "pwa.update_available": { keys: ["version"] },
  "pwa.update_accepted": { keys: [] },
  "pwa.worker_registered": { keys: [] },
  "pwa.worker_registration_failed": { keys: ["reason"] },
  "pwa.offline": { keys: [] },
  "pwa.connection_restored": { keys: [] },
  "push.permission_result": { keys: ["platform", "outcome"] },
  "push.subscribed": { keys: ["platform"] },
  "push.subscription_failed": { keys: ["platform", "reason"] },
  "push.unsubscribed": { keys: ["platform"] },
  "push.opened": { keys: ["campaign"] },
  // Command menu and keyboard
  "command.opened": { keys: ["via"] },
  "command.selected": { keys: ["command"] },
  "shortcut.used": { keys: ["shortcut"], sample: 0.1 },
  // Diagnostics (30-day retention)
  "app.error_boundary": { tier: "diagnostics", keys: ["route", "kind"] },
  "api.request_failed": {
    tier: "diagnostics",
    keys: ["endpoint", "status_class", "dpop"],
    sample: 0.2,
  },
  "web_vitals.reported": {
    tier: "diagnostics",
    keys: ["metric", "value_band", "route"],
    sample: 0.1,
  },
  "page.loading_slow": { tier: "diagnostics", keys: ["route", "ms_band"] },
} as const;

type Registry = typeof JOURNEY_EVENTS;
export type JourneyEventName = keyof Registry;
type EventSpec = {
  keys: readonly string[];
  v?: number;
  tier?: "core" | "diagnostics";
  sample?: number;
};
type PropValue = string | number | boolean | null | undefined;
export type JourneyProps<N extends JourneyEventName> = Partial<
  Record<Registry[N]["keys"][number], PropValue>
>;

// Sent even under Global Privacy Control: they feed the user's own link stats.
const ALWAYS_SENT = new Set<string>([
  "link_copied",
  "link_shared",
  "shared_link_visited",
]);

const SOURCE = "fexr_web";
const POLICY_VERSION = "web-analytics-1";
const QUEUE_KEY = "fexr_journey_queue";
const LEGACY_QUEUE_KEY = "fexr_analytics_queue";
const QUEUE_MAX = 300;
const QUEUE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // fexrapi accepts up to 7 days
const BATCH_MAX = 50;
const MAX_ATTEMPTS = 5;
const FLUSH_DELAY_MS = 5_000;
const FLUSH_AT = 20;
const SESSION_KEY = "fexr_journey_session_v2";
const SESSION_IDLE_MS = 30 * 60 * 1000;
const ANON_KEY = "fexr_anon_id";
const AUTH_METHOD_KEY = "fexr_auth_method";
const isDev = process.env.NODE_ENV !== "production";

// ── Helpers ──────────────────────────────────────────────────────────────────

// fexrapi requires real UUIDs.
export function uuidV4(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

function readJSON<T>(storage: Storage, key: string): T | null {
  try {
    const raw = storage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJSON(storage: Storage, key: string, value: unknown) {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {}
}

/** Global Privacy Control or declined analytics cookies: send only the link events the user's own stats need. */
export function privacyOptOut(): boolean {
  if (typeof navigator === "undefined") return false;
  return (
    (navigator as Navigator & { globalPrivacyControl?: boolean })
      .globalPrivacyControl === true || consentChoice() === false
  );
}

const SEGMENT_ID =
  /^(0x[0-9a-f]+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+|[A-Za-z0-9_-]{20,})$/i;
// Routes whose dynamic segment is a token, an id or an address.
const TEMPLATED: [RegExp, string][] = [
  [/^\/w\/(?!board$)[^/]+/, "/w/[id]"],
  [/^\/vaults\/[^/]+/, "/vaults/[id]"],
  [/^\/pay\/[^/]+/, "/pay/[id]"],
  [/^\/analysis\/[^/]+/, "/analysis/[token]"],
  [/^\/response\/[^/]+/, "/response/[code]"],
  [/^\/link-entry\/.+/, "/link-entry/[token]"],
  [/^\/invitations\/workspace\/[^/]+/, "/invitations/workspace/[token]"],
  [/^\/invitations\/[^/]+/, "/invitations/[token]"],
  [/^\/unsubscribe\/[^/]+/, "/unsubscribe/[token]"],
];

/**
 * A pathname as a route template: ids, tokens and addresses become [id],
 * so analytics never stores them. Capped at fexrapi's 100-char `screen`.
 */
export function routeTemplate(pathname: string): string {
  let path = (pathname || "/").split("?")[0]!.split("#")[0]!;
  for (const [re, tpl] of TEMPLATED) {
    if (re.test(path)) {
      path = path.replace(re, tpl);
      break;
    }
  }
  path = path
    .split("/")
    .map((seg) =>
      seg && !seg.startsWith("[") && SEGMENT_ID.test(seg) ? "[id]" : seg,
    )
    .join("/");
  return path.slice(0, 100) || "/";
}

function layoutClass(): string {
  const w = window.innerWidth;
  return w < 640 ? "compact" : w < 1024 ? "medium" : "wide";
}

function clientEnvelope() {
  return {
    app_version: (process.env.NEXT_PUBLIC_APP_VERSION || "dev").slice(0, 40),
    platform: "web",
    locale: (navigator.language || "en").slice(0, 16),
    layout_class: layoutClass(),
  };
}

/** One random id per browser, joining pre-signup activity to the account. */
export function anonymousId(): string {
  try {
    const existing = localStorage.getItem(ANON_KEY);
    if (existing) return existing;
    const id = uuidV4();
    localStorage.setItem(ANON_KEY, id);
    return id;
  } catch {
    return uuidV4();
  }
}

function hadAnonymousId(): boolean {
  try {
    return !!localStorage.getItem(ANON_KEY);
  } catch {
    return false;
  }
}

// ── Journey session ──────────────────────────────────────────────────────────
// Per tab; a new one starts after 30 minutes idle. Starting one queues
// app.session.started first.

interface JourneySession {
  id: string;
  seq: number;
  last: number;
  entry: string;
}

function nextSessionSlot(screen: string): {
  session: JourneySession;
  started: boolean;
} {
  const now = Date.now();
  let s: JourneySession | null = null;
  try {
    s = readJSON<JourneySession>(sessionStorage, SESSION_KEY);
  } catch {}
  let started = false;
  if (!s || now - s.last > SESSION_IDLE_MS) {
    s = { id: uuidV4(), seq: 0, last: now, entry: screen };
    started = true;
  }
  s.seq += 1;
  s.last = now;
  try {
    writeJSON(sessionStorage, SESSION_KEY, s);
  } catch {}
  return { session: s, started };
}

// ── Queue ────────────────────────────────────────────────────────────────────

interface QueuedEvent {
  event_id: string;
  event_name: string;
  event_version: number;
  occurred_at: string;
  session_id: string;
  sequence: number;
  source: typeof SOURCE;
  source_surface: string;
  screen: string;
  entry_point: string;
  correlation: { trace_id: string; entity_type?: string; entity_ref?: string };
  properties: Record<string, string | number | boolean>;
  client: Record<string, string>;
  privacy: { tier: string; policy_version: string };
  attempts?: number;
  queued_at: number;
}

function readQueue(): QueuedEvent[] {
  try {
    const parsed = readJSON<QueuedEvent[]>(localStorage, QUEUE_KEY) ?? [];
    const cutoff = Date.now() - QUEUE_TTL_MS;
    return parsed.filter((e) => e?.event_id && e.queued_at > cutoff);
  } catch {
    return [];
  }
}

function writeQueue(events: QueuedEvent[]) {
  if (events.length > QUEUE_MAX) {
    // Evict the most common, least useful first.
    const cheap = (e: QueuedEvent) =>
      e.event_name === "screen.viewed" || e.privacy.tier === "diagnostics";
    let excess = events.length - QUEUE_MAX;
    events = events.filter((e) => !(excess > 0 && cheap(e) && excess--));
    events = events.slice(-QUEUE_MAX);
  }
  writeJSON(localStorage, QUEUE_KEY, events);
}

function standalone(): boolean {
  try {
    return window.matchMedia("(display-mode: standalone)").matches;
  } catch {
    return false;
  }
}

/**
 * The thing an event is about. fexrapi's facilitated_value_events view and the
 * growth dashboard's decision-loop and signal → trade panels join on
 * (user, entity_type, entity_ref): an open and the action that follows it must
 * name the same entity. Signals use the signal's UUID.
 */
export interface JourneyEntity {
  type: "signal" | "answer" | "asset" | "strategy" | "vault" | "trader_move";
  ref: string;
}

// fexrapi rejects raw 0x addresses/hashes anywhere in an event.
const RAW_CHAIN = /^0x([0-9a-f]{40}|[0-9a-f]{64})$/i;

function buildEvent(
  name: string,
  spec: EventSpec,
  props: Record<string, PropValue> | undefined,
  screen: string,
  entity?: JourneyEntity,
): QueuedEvent[] {
  const { session, started } = nextSessionSlot(screen);
  const make = (
    eventName: string,
    version: number,
    tier: string,
    properties: Record<string, string | number | boolean>,
    sequence: number,
  ): QueuedEvent => ({
    event_id: uuidV4(),
    event_name: eventName,
    event_version: version,
    occurred_at: new Date().toISOString(),
    session_id: session.id,
    sequence,
    source: SOURCE,
    source_surface: standalone() ? "pwa" : "web",
    screen,
    entry_point: session.entry,
    correlation: {
      trace_id: anonymousId(),
      ...(entity &&
      eventName === name &&
      entity.ref &&
      !RAW_CHAIN.test(entity.ref)
        ? {
            entity_type: entity.type,
            entity_ref: String(entity.ref).slice(0, 128),
          }
        : {}),
    },
    properties,
    client: clientEnvelope(),
    privacy: { tier, policy_version: POLICY_VERSION },
    queued_at: Date.now(),
  });

  const properties: Record<string, string | number | boolean> = {};
  for (const key of spec.keys) {
    const value = props?.[key];
    if (value === undefined || value === null) continue;
    properties[key] = typeof value === "string" ? value.slice(0, 256) : value;
  }
  const out: QueuedEvent[] = [];
  if (started && name !== "app.session.started") {
    out.push(make("app.session.started", 1, "core", {}, session.seq));
    // The started marker takes this sequence; the event takes the next.
    const bumped = nextSessionSlot(screen).session;
    out.push(
      make(name, spec.v ?? 1, spec.tier ?? "core", properties, bumped.seq),
    );
  } else {
    out.push(
      make(name, spec.v ?? 1, spec.tier ?? "core", properties, session.seq),
    );
  }
  return out;
}

let lastScreenView: { route: string; at: number } | null = null;

/**
 * Record a cataloged journey event. Fire-and-forget; safe anywhere client-side.
 * Queued locally and sent once there is a session (immediately when signed in).
 */
export function trackEvent<N extends JourneyEventName>(
  name: N,
  props?: JourneyProps<N>,
  entity?: JourneyEntity,
): void {
  if (typeof window === "undefined") return;
  const spec = JOURNEY_EVENTS[name] as EventSpec | undefined;
  if (!spec) return;
  if (privacyOptOut() && !ALWAYS_SENT.has(name)) return;
  if (spec.sample !== undefined && Math.random() >= spec.sample) return;
  try {
    const screen = routeTemplate(window.location.pathname);
    if (name === "screen.viewed") {
      const route = String((props as { route?: string })?.route ?? screen);
      if (
        lastScreenView?.route === route &&
        Date.now() - lastScreenView.at < 3_000
      )
        return;
      lastScreenView = { route, at: Date.now() };
    }
    const events = buildEvent(
      name,
      spec,
      props as Record<string, PropValue> | undefined,
      screen,
      entity,
    );
    writeQueue([...readQueue(), ...events]);
    if (isDev) console.debug("[journey]", name, events.at(-1)?.properties);
    scheduleFlush();
  } catch {
    // Never block the caller on analytics storage.
  }
}

// API failures (sampled, diagnostics tier): endpoint template, status class.
if (typeof window !== "undefined") {
  setRequestFailureReporter((endpoint, status, dpop) =>
    trackEvent("api.request_failed", {
      endpoint: routeTemplate(endpoint.split("?")[0] ?? endpoint),
      status_class: status === 0 ? "network" : status >= 500 ? "5xx" : "4xx",
      dpop,
    }),
  );
}

// ── Flush ────────────────────────────────────────────────────────────────────

// The latest signed-in session, kept by useAnalyticsTracker so flushes can
// run outside React (debounce timer, tab hide, sign-out).
let currentSession: any = null;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushing = false;

function scheduleFlush() {
  if (!currentSession) return;
  if (readQueue().length >= FLUSH_AT) {
    void flushQueue();
    return;
  }
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushQueue();
  }, FLUSH_DELAY_MS);
}

function wire(e: QueuedEvent) {
  const { attempts: _a, queued_at: _q, ...body } = e;
  return body;
}

async function postBatch(events: QueuedEvent[]): Promise<Response> {
  const endpoint = "/v1/journeys/events/batch";
  const headers = await dpopApi.authHeaders("POST", endpoint, currentSession);
  // keepalive lets a flush started on tab hide finish after the page goes away.
  return fetch(publicApi.url(endpoint), {
    method: "POST",
    keepalive: true,
    credentials: "include",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify({ events: events.map(wire) }),
  });
}

/** Send queued events. Keeps retryable failures; drops what fexrapi rejected. */
export async function flushQueue(): Promise<void> {
  if (flushing || !currentSession?.fexrApiAccessToken) return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  flushing = true;
  const run = async () => {
    const pending = readQueue();
    for (let i = 0; i < pending.length; i += BATCH_MAX) {
      const chunk = pending.slice(i, i + BATCH_MAX);
      const ids = new Set(chunk.map((e) => e.event_id));
      let done = new Set<string>();
      let retry = false;
      try {
        const res = await postBatch(chunk);
        if (res.ok) {
          const body = await res.json().catch(() => null);
          done = new Set([
            ...(body?.accepted ?? []),
            ...(body?.duplicates ?? []),
            ...((body?.rejected ?? []) as { event_id: string }[]).map(
              (r) => r.event_id,
            ),
          ]);
          if (isDev && body?.rejected?.length) {
            const byId = new Map(chunk.map((e) => [e.event_id, e.event_name]));
            console.warn(
              "[journey] rejected",
              body.rejected.map(
                (r: { event_id: string; code: string }) =>
                  `${byId.get(r.event_id)}: ${r.code}`,
              ),
            );
          }
          // Anything the response didn't mention is kept for a retry.
          retry = done.size < ids.size;
        } else if (
          res.status === 401 ||
          res.status === 429 ||
          res.status >= 500
        ) {
          retry = true;
        } else {
          // A 4xx for the whole batch (malformed envelope) would never pass.
          done = ids;
          if (isDev)
            console.warn(
              "[journey] batch refused",
              res.status,
              await res.text().catch(() => ""),
            );
        }
      } catch {
        retry = true;
      }
      // Re-read: other tabs and new events may have changed the queue.
      writeQueue(
        readQueue()
          .filter((e) => !done.has(e.event_id))
          .map((e) =>
            ids.has(e.event_id) && retry
              ? { ...e, attempts: (e.attempts ?? 0) + 1 }
              : e,
          )
          .filter((e) => (e.attempts ?? 0) < MAX_ATTEMPTS),
      );
      if (retry) break;
    }
  };
  try {
    const locks = (navigator as Navigator & { locks?: LockManager }).locks;
    if (locks?.request) {
      // One tab flushes at a time; the others skip rather than double-send.
      await locks.request(
        "fexr-journey-flush",
        { ifAvailable: true },
        (lock) => (lock ? run() : undefined),
      );
    } else {
      await run();
    }
  } finally {
    flushing = false;
  }
}

/** Record sign-out and send what's queued before storage is cleared. */
export async function flushBeforeSignOut(): Promise<void> {
  trackEvent("auth.signed_out");
  await Promise.race([
    flushQueue(),
    new Promise((resolve) => setTimeout(resolve, 1_500)),
  ]);
}

/** Remember the sign-in method chosen so the check-in can report it. */
export function rememberAuthMethod(
  method:
    | "google"
    | "email_link"
    | "wallet"
    | "fexr_wallet"
    | "telegram"
    | "enterprise",
  mode: "login" | "register",
  hasFrom: boolean,
): void {
  try {
    localStorage.setItem(
      AUTH_METHOD_KEY,
      JSON.stringify({
        method,
        from: routeTemplate(window.location.pathname),
        at: Date.now(),
      }),
    );
  } catch {}
  trackEvent("auth.started", { method, mode, has_from: hasFrom });
}

/** Mount once in the root layout: keeps the session and flushes the queue. */
export function useAnalyticsTracker(): void {
  const { data: session, status } = useSession();

  useEffect(() => {
    // One-time migration: the old queue's events had no ids or screens.
    try {
      localStorage.removeItem(LEGACY_QUEUE_KEY);
      localStorage.removeItem("fexr_ai_arrival");
    } catch {}
  }, []);

  useEffect(() => {
    currentSession =
      status === "authenticated" && session?.fexrApiAccessToken
        ? session
        : null;
    if (currentSession) void flushQueue();
  }, [session, status]);

  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === "hidden") void flushQueue();
    };
    const onOnline = () => void flushQueue();
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onHide);
    window.addEventListener("online", onOnline);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("online", onOnline);
    };
  }, []);
}

// ── Arrivals and check-in ────────────────────────────────────────────────────
// The first page of a browser session that came from outside (UTM-tagged link
// or another site) is posted to POST /v1/journeys/arrivals, signed in or not;
// fexrapi records it as acquisition.landed (and ai_referral_landed for AI
// answer engines). It is kept for 30 days and posted again with the session
// after sign-in (acquisition.signed_in, first/last-touch attribution). Every
// signed-in browser session also checks in once, which records
// account.created for a new account and auth.completed here.

const ARRIVAL_SEEN_KEY = "fexr_arrival_seen";
const ACQUISITION_KEY = "fexr_acquisition";
const ACQUISITION_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const CHECKIN_KEY = "fexr_checkin";
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;
const OWN_HOSTS = /(^|\.)(fexr\.io|fexr\.club|fexrclub\.com|localhost)$/i;
const AUTH_HOSTS =
  /(^|\.)(accounts\.google\.com|oauth\.telegram\.org|telegram\.org|appleid\.apple\.com)$/i;

interface Arrival {
  navigation_id: string;
  path: string;
  referrer_host?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  is_first_visit?: boolean;
  at: number;
  signed_in?: boolean;
}

function readAcquisition(): Arrival | null {
  try {
    const a = readJSON<Arrival>(localStorage, ACQUISITION_KEY);
    return a && Date.now() - a.at < ACQUISITION_TTL_MS ? a : null;
  } catch {
    return null;
  }
}

function arrivalBody(a: Arrival, authMethod?: string) {
  const { at: _at, signed_in: _s, ...body } = a;
  return authMethod ? { ...body, auth_method: authMethod } : body;
}

function captureArrival(): Arrival | null {
  let host: string | undefined;
  try {
    const r = document.referrer ? new URL(document.referrer) : null;
    if (
      r &&
      r.host !== window.location.host &&
      !OWN_HOSTS.test(r.hostname) &&
      !AUTH_HOSTS.test(r.hostname)
    )
      host = r.hostname.toLowerCase();
  } catch {}
  const params = new URLSearchParams(window.location.search);
  const utm: Partial<Record<(typeof UTM_KEYS)[number], string>> = {};
  for (const k of UTM_KEYS) {
    const v = params.get(k)?.trim();
    if (v) utm[k] = v.slice(0, 100);
  }
  if (!host && Object.keys(utm).length === 0) return null;
  const firstVisit = !hadAnonymousId();
  anonymousId();
  return {
    navigation_id: uuidV4(),
    path: routeTemplate(window.location.pathname),
    ...(host ? { referrer_host: host } : {}),
    ...utm,
    is_first_visit: firstVisit,
    at: Date.now(),
  };
}

/** Drop utm_* from the address bar so copied URLs don't carry the campaign. */
function stripUtm() {
  try {
    const url = new URL(window.location.href);
    let changed = false;
    for (const k of UTM_KEYS)
      if (url.searchParams.has(k)) {
        url.searchParams.delete(k);
        changed = true;
      }
    if (changed)
      window.history.replaceState(window.history.state, "", url.toString());
  } catch {}
}

function readAuthMethod(): { method: string; from: string; at: number } | null {
  try {
    const m = readJSON<{ method: string; from: string; at: number }>(
      localStorage,
      AUTH_METHOD_KEY,
    );
    // A sign-in started more than a day ago isn't this one.
    return m && Date.now() - m.at < 24 * 60 * 60 * 1000 ? m : null;
  } catch {
    return null;
  }
}

export function useArrivalTracker(): void {
  const { data: session, status } = useSession();

  // First page of the browser session: capture and post the arrival.
  useEffect(() => {
    if (privacyOptOut()) return;
    try {
      if (sessionStorage.getItem(ARRIVAL_SEEN_KEY)) return;
    } catch {
      return;
    }
    const arrival = captureArrival();
    anonymousId();
    try {
      sessionStorage.setItem(ARRIVAL_SEEN_KEY, "1");
    } catch {}
    if (!arrival) return;
    stripUtm();
    writeJSON(localStorage, ACQUISITION_KEY, arrival);
    // Signed-in visitors are posted by the check-in below, with the session.
    if (status === "authenticated") return;
    publicApi.post("/v1/journeys/arrivals", arrivalBody(arrival)).catch(() => {
      try {
        sessionStorage.removeItem(ARRIVAL_SEEN_KEY);
      } catch {}
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once per signed-in browser session: the check-in.
  useEffect(() => {
    if (status !== "authenticated" || !session?.fexrApiAccessToken) return;
    if (privacyOptOut()) return;
    const userId = String(session.user?.id ?? "");
    try {
      if (sessionStorage.getItem(CHECKIN_KEY) === userId) return;
    } catch {
      return;
    }
    const acquisition = readAcquisition();
    const pendingAuth = readAuthMethod();
    const arrival =
      acquisition && !acquisition.signed_in
        ? acquisition
        : {
            navigation_id: uuidV4(),
            path: routeTemplate(window.location.pathname),
            at: Date.now(),
          };
    dpopApi
      .post(
        "/v1/journeys/arrivals",
        arrivalBody(arrival, pendingAuth?.method),
        session,
      )
      .then(async (r) => {
        if (!r.ok) return;
        const d = await r.json().catch(() => null);
        try {
          sessionStorage.setItem(CHECKIN_KEY, userId);
        } catch {}
        if (acquisition && !acquisition.signed_in)
          writeJSON(localStorage, ACQUISITION_KEY, {
            ...acquisition,
            signed_in: true,
          });
        if (pendingAuth) {
          trackEvent("auth.completed", {
            method: pendingAuth.method,
            is_new_account: !!d?.account_created,
            from_route: pendingAuth.from,
          });
          try {
            localStorage.removeItem(AUTH_METHOD_KEY);
          } catch {}
        }
      })
      .catch(() => {});
  }, [session, status]);
}

// ── Bands ────────────────────────────────────────────────────────────────────
// Counts and durations as coarse bands, so no event carries an exact figure.

export function countBand(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "unknown";
  if (n <= 0) return "0";
  if (n === 1) return "1";
  if (n <= 5) return "2-5";
  if (n <= 20) return "6-20";
  if (n <= 100) return "21-100";
  return "100+";
}

export function msBand(ms: number): string {
  if (ms < 1_000) return "<1s";
  if (ms < 3_000) return "1-3s";
  if (ms < 10_000) return "3-10s";
  if (ms < 30_000) return "10-30s";
  if (ms < 120_000) return "30-120s";
  return "120s+";
}

/** How long something existed before an action on it (a strategy paused). */
export function ageBand(createdAt: string | null | undefined): string {
  const t = createdAt ? new Date(createdAt).getTime() : NaN;
  if (Number.isNaN(t)) return "unknown";
  const days = (Date.now() - t) / 86_400_000;
  if (days < 1) return "<1d";
  if (days < 7) return "1-7d";
  if (days < 30) return "7-30d";
  return "30d+";
}

export function rankBand(rank: number | null | undefined): string {
  if (!rank || rank < 1) return "unranked";
  if (rank <= 10) return "top10";
  if (rank <= 50) return "top50";
  if (rank <= 100) return "top100";
  return "100+";
}
